import React from 'react';
import {Table, Tooltip, Typography, Tag} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {ReportFileType} from "../graphql/queries/getReportFileListingForWms";
import dayjs from 'dayjs';
import {ReportFileTypeEnum, ReportFileStatusEnum} from "../../../../enums/ReportFileEnum";
import { useTranslation } from 'react-i18next';

interface WmsReportColumnsProps {
    reportFileData: ReportFileType[];
    onSelectChange: (selectedRowKeys: React.Key[], selectedRows: ReportFileType[]) => void;
    selectedRowKeys: React.Key[];
    loading: boolean;
}

const WmsReportFileColumns: React.FC<WmsReportColumnsProps> = ({
    reportFileData,
    onSelectChange,
    selectedRowKeys,
    loading,
}) => {
    const {t} = useTranslation();
    const getReportFileTypeTag = (type: number) => {
        switch (type) {
            case ReportFileTypeEnum.DAILY:
                return <Tag color="blue">{t('enum.reportTypeOptions.daily')}</Tag>;
            case ReportFileTypeEnum.WEEKLY:
                return <Tag color="green">{t('enum.reportTypeOptions.weekly')}</Tag>;
            case ReportFileTypeEnum.MONTHLY:
                return <Tag color="orange">{t('enum.reportTypeOptions.monthly')}</Tag>;
            case ReportFileTypeEnum.PARCEL_DOWNLOAD:
                return <Tag color="purple">{t('enum.reportTypeOptions.parcelDownload')}</Tag>;
            case ReportFileTypeEnum.DASHBOARD_EXPORT:
                return <Tag color="cyan">{t('enum.reportTypeOptions.dashboardExport')}</Tag>;
            case ReportFileTypeEnum.EXCEPTION_DOWNLOAD:
                return <Tag color="volcano">{t('enum.reportTypeOptions.exceptionDownload')}</Tag>;
            case ReportFileTypeEnum.OTHER_CARRIER_BAG_TASK:
                return <Tag color="cyan">{t('enum.reportTypeOptions.otherCarrierBagTask')}</Tag>;
            case ReportFileTypeEnum.BAG_TAG_EXPORT:
                return <Tag color="geekblue">{t('enum.reportTypeOptions.bagTagExport')}</Tag>;
            case ReportFileTypeEnum.WAREHOUSE_LOCATION_EXPORT:
                return <Tag color="purple">{t('enum.reportTypeOptions.warehouseLocationExport')}</Tag>;
            case ReportFileTypeEnum.DELIVERY_TASK_TRACKING_NUMBER_EXPORT:
                return <Tag color="gold">{t('enum.reportTypeOptions.deliveryTaskTrackingNumberExport')}</Tag>;
            case ReportFileTypeEnum.PARCEL_SHIPPING_LABEL_PDF:
                return <Tag color="purple">{t('enum.reportTypeOptions.parcelShippingLabelPdf')}</Tag>;
            case ReportFileTypeEnum.DSP_SUMMARY_EXPORT:
                return <Tag color="blue">{t('enum.reportTypeOptions.dspSummaryExport')}</Tag>;
            case ReportFileTypeEnum.ROUTE_ORDER_POOL_EXPORT:
                return <Tag color="geekblue">{t('enum.reportTypeOptions.routeOrderPoolExport')}</Tag>;
            default:
                return <Tag color="default">{t('enum.reportTypeOptions.other')}</Tag>;
        }
    };

    const getStatusTag = (status: number) => {
        switch (status) {
            case ReportFileStatusEnum.DOWNLOADING:
                return <Tag color="processing">{t('enum.reportFileStatusOptions.downloading')}</Tag>;
            case ReportFileStatusEnum.COMPLETED:
                return <Tag color="success">{t('enum.reportFileStatusOptions.completed')}</Tag>;
            case ReportFileStatusEnum.FAILED:
                return <Tag color="error">{t('enum.reportFileStatusOptions.failed')}</Tag>;
            default:
                return <Tag color="default">{t('enum.reportFileStatusOptions.other')}</Tag>;
        }
    };

    const formatDate = (dateString: string) => {
        return dayjs(dateString).format('YYYY-MM-DD');
    };

    const columns: ColumnsType<ReportFileType> = [
        {
            title: t('wms.download.columns.reportDate'),
            dataIndex: 'reportDate',
            key: 'reportDate',
            width: 120,
            align: 'left' as const,
            ellipsis: false,
            render: (dateString: string) => {
                const text = formatDate(dateString);
                return <span style={{ whiteSpace: 'nowrap' }}>{text}</span>;
            },
        },
        {
            title: t('wms.download.columns.type'),
            dataIndex: 'type',
            key: 'type',
            width: 150,
            align: 'left' as const,
            ellipsis: false,
            render: (type: number) => getReportFileTypeTag(type),
        },
        {
            title: t('wms.download.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 120,
            align: 'left' as const,
            ellipsis: false,
            render: (status: number) => getStatusTag(status),
        },
        {
            title: t('wms.download.columns.reportName'),
            dataIndex: 'name',
            key: 'name',
            width: 420,
            align: 'left' as const,
            ellipsis: false,
            render: (name?: string) => (
                <Tooltip title={name || '-'}>
                    <Typography.Text ellipsis>
                        {name || '-'}
                    </Typography.Text>
                </Tooltip>
            ),
        },
        {
            title: t('wms.download.filters.operator'),
            dataIndex: 'operatorName',
            key: 'operatorName',
            width: 150,
            align: 'left' as const,
            ellipsis: false,
            render: (operatorName?: string) => <span style={{ whiteSpace: 'nowrap' }}>{operatorName || '-'}</span>
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record: ReportFileType) => ({
            disabled: record.status === ReportFileStatusEnum.DOWNLOADING || record.status === ReportFileStatusEnum.FAILED, // 下载中和下载失败不允许选中
        }),
    };

    return (
        <div className="[&_.ant-table-body]:overflow-scroll [&_.ant-table-body]:overflow-x-scroll [&_.ant-table-body]:overflow-y-scroll">
            <Table
                columns={columns}
                dataSource={reportFileData}
                rowKey="id"
                loading={loading}
                rowSelection={rowSelection}
                pagination={false}
                scroll={{ x: 'max-content' }}
                size="middle"
                tableLayout="auto"
             className="mb-4"/>
        </div>
    );
};

export default WmsReportFileColumns;